import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    CalendarDaysIcon,
    CurrencyDollarIcon,
    MapPinIcon,
} from "@heroicons/react/24/solid";
import { PageContainer } from "../components/PageContainer";
import { jobService } from "../services/job.service";
import { IJobView } from "../interfaces/job/iJobView.interface";
import { useAsyncData } from "../hooks/useAsyncData";
import {
    infoCardClasses,
    pageLeadClasses,
    sectionCardCompactClasses,
    subtleEyebrowClasses,
} from "../styles/pageClasses";
import {
    primaryBtnModernClasses,
    secondaryBtnModernClasses,
} from "../styles/uiClasses";
import { jobDetailsPageClasses } from "../styles/pageSpecificClasses";

export default function JobDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { data: job, loading, error: fetchError } = useAsyncData<IJobView>(
        () => jobService.get(Number(id)),
        [id]
    );

    const fmt = (d?: string | Date) => (d ? new Date(d).toLocaleDateString() : "");

    if (loading) {
        return (
            <div className={jobDetailsPageClasses.loading}>
                Loading job details...
            </div>
        );
    }

    if (fetchError || !job) {
        return (
            <div className={jobDetailsPageClasses.errorWrap}>
                <p className={jobDetailsPageClasses.errorCopy}>{fetchError ?? "Job not found."}</p>
                <Link to="/" className={jobDetailsPageClasses.backLink}>
                    Back to Jobs
                </Link>
            </div>
        );
    }

    return (
        <PageContainer maxWidth="5xl" contentClassName={jobDetailsPageClasses.shellSpacing}>
            <div>
                <button
                    onClick={() => navigate(-1)}
                    className={jobDetailsPageClasses.backButton}
                >
                    Back to Jobs
                </button>
            </div>

            <section className={infoCardClasses}>
                <span className={subtleEyebrowClasses}>
                    {job.jobCategoryName ?? "Seasonal Role"}
                </span>

                <div className={jobDetailsPageClasses.titleBlock}>
                    <h1 className={jobDetailsPageClasses.title}>
                        {job.title}
                    </h1>
                    {job.companyName && (
                        <p className={jobDetailsPageClasses.company}>{job.companyName}</p>
                    )}
                </div>

                <div className={jobDetailsPageClasses.metaGrid}>
                    <div className={sectionCardCompactClasses}>
                        <div className={jobDetailsPageClasses.metaRow}>
                            <MapPinIcon className={jobDetailsPageClasses.metaIcon} />
                            <span>
                                {job.locationName}
                                {job.locationGreekName ? ` (${job.locationGreekName})` : ""},{" "}
                                {job.locationRegionName}
                                {job.locationRegionGreekName ? ` (${job.locationRegionGreekName})` : ""}
                            </span>
                        </div>
                    </div>

                    {typeof job.salary === "number" && (
                        <div className={sectionCardCompactClasses}>
                            <div className={jobDetailsPageClasses.metaRow}>
                                <CurrencyDollarIcon className={jobDetailsPageClasses.metaIcon} />
                                <span>EUR {Number(job.salary).toLocaleString()}</span>
                            </div>
                        </div>
                    )}

                    {job.createdDate && (
                        <div className={sectionCardCompactClasses}>
                            <div className={jobDetailsPageClasses.metaRow}>
                                <CalendarDaysIcon className={jobDetailsPageClasses.metaIcon} />
                                <span>Posted: {fmt(job.createdDate)}</span>
                            </div>
                        </div>
                    )}

                    {job.effectiveFrom && (
                        <div className={sectionCardCompactClasses}>
                            <div className={jobDetailsPageClasses.metaRow}>
                                <CalendarDaysIcon className={jobDetailsPageClasses.metaIcon} />
                                <span>Active from: {fmt(job.effectiveFrom)}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className={jobDetailsPageClasses.actions}>
                    {/*
                    Restore these when logged-in user features return:
                    <a href={`/job/${job.id}/apply`} className={primaryBtnModernClasses}>
                        Apply Now
                    </a>
                    <button
                        onClick={handleSaveJob}
                        disabled={isSaving || job.isSaved}
                        className={`${secondaryBtnModernClasses} ${jobDetailsPageClasses.disabledButton} ${job.isSaved ? jobDetailsPageClasses.savedButtonActive : ""}`}
                    >
                        <BookmarkIcon
                            className={`h-5 w-5 ${job.isSaved ? jobDetailsPageClasses.savedIconActive : jobDetailsPageClasses.savedIconIdle}`}
                        />
                        <span>{job.isSaved ? "Saved" : isSaving ? "Saving..." : "Save Job"}</span>
                    </button>
                    */}
                    <a href={`mailto:${job.email}`} className={primaryBtnModernClasses}>
                        Contact Employer
                    </a>
                    {job.phone && (
                        <a href={`tel:${job.phone}`} className={secondaryBtnModernClasses}>
                            Call Employer
                        </a>
                    )}
                </div>
            </section>

            <section className={infoCardClasses}>
                <h2 className={jobDetailsPageClasses.descriptionTitle}>
                    Job Description
                </h2>
                <p className={`${jobDetailsPageClasses.descriptionCopy} ${pageLeadClasses}`}>{job.description}</p>
            </section>

            <div className={jobDetailsPageClasses.mobileBar}>
                <div className={jobDetailsPageClasses.mobileBarInner}>
                    {/*
                    Previous mobile CTA:
                    <a
                        href={`/job/${job.id}/apply`}
                        className={`${jobDetailsPageClasses.mobilePrimary} ${primaryBtnModernClasses}`}
                    >
                        Apply Now
                    </a>
                    */}
                    <a
                        href={`mailto:${job.email}`}
                        className={`${jobDetailsPageClasses.mobilePrimary} ${primaryBtnModernClasses}`}
                    >
                        Contact
                    </a>
                </div>
            </div>
        </PageContainer>
    );
}
